import React from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Stack,
    TextField,
} from "@mui/material";
import {Priority} from "~/sit/ApiTypes.ts";
import type {
    PtV2Action,
    PtV2CreateInitiativeResult,
    PtV2InitiativeOptions,
    PtV2PlanItem,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import type {PtV2InitiativeDraft} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Initiative.ts";
import {PT_V2_DEFAULT_PRIORITY} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Initiative.ts";
import {usePtV2CreateInitiativeMutation} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";

type Props = {
    item: PtV2PlanItem;
    /** The stored action for `item.key`, when it is already in the plan. */
    existing: PtV2Action | undefined;
    draft: PtV2InitiativeDraft;
    options: PtV2InitiativeOptions;
    onCreated: (result: PtV2CreateInitiativeResult) => void;
    onClose: () => void;
};

/** The backend's message for a 409 (no SIT profile), 503 (SIT disabled) or 400. */
function errorMessage(e: unknown): string {
    const data = (e as {response?: {data?: unknown}}).response?.data;
    if (data && typeof data === 'object') {
        const o = data as Record<string, unknown>;
        if (typeof o.detail === 'string') return o.detail;
        const first = Object.entries(o)[0];
        if (first) return `${first[0]}: ${Array.isArray(first[1]) ? first[1].join(' ') : String(first[1])}`;
    }
    return 'The initiative could not be created. Try again.';
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Pre-filled form for a Savings Initiative Tracker draft. Mounted only while open, so each opening starts clean. */
export const PtV2InitiativeDialog: React.FC<Props> = ({item, existing, draft, options, onCreated, onClose}) => {
    const createM = usePtV2CreateInitiativeMutation();
    const [title, setTitle] = React.useState(draft.title);
    const [description, setDescription] = React.useState(draft.description);
    const [lever, setLever] = React.useState(options.defaultLever ?? options.levers[0] ?? '');
    const [priority, setPriority] = React.useState(PT_V2_DEFAULT_PRIORITY);
    const [responsibleEmail, setResponsibleEmail] = React.useState('');
    const [approverEmail, setApproverEmail] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);

    const badEmail = (v: string) => v.trim() !== '' && !EMAIL.test(v.trim());
    const valid = title.trim() && description.trim() && lever && !badEmail(responsibleEmail) && !badEmail(approverEmail);

    const submit = async () => {
        setError(null);
        try {
            const result = await createM.create(item, existing, {
                title: title.trim(),
                description: description.trim(),
                lever,
                priority,
                ...(responsibleEmail.trim() ? {responsibleEmail: responsibleEmail.trim()} : {}),
                ...(approverEmail.trim() ? {approverEmail: approverEmail.trim()} : {}),
            });
            onCreated(result);
        } catch (e) {
            setError(errorMessage(e));
        }
    };

    return <Dialog open onClose={createM.isPending ? undefined : onClose} maxWidth="sm" fullWidth
                   sx={theme => ({zIndex: theme.zIndex.drawer + 3})}>
        <DialogTitle>Create savings initiative</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{pt: 1}}>
                <TextField label="Title" size="small" required value={title} slotProps={{htmlInput: {maxLength: 256}}}
                           onChange={e => setTitle(e.target.value)}/>
                <TextField label="Description" size="small" required multiline minRows={5} value={description}
                           onChange={e => setDescription(e.target.value)}/>
                <Stack direction="row" spacing={2}>
                    <TextField select label="Lever" size="small" required value={lever} sx={{flex: 2}}
                               onChange={e => setLever(e.target.value)}>
                        {options.levers.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </TextField>
                    <TextField select label="Priority" size="small" value={priority} sx={{flex: 1}}
                               onChange={e => setPriority(e.target.value)}>
                        {Priority.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </TextField>
                </Stack>
                <Stack direction="row" spacing={2}>
                    <TextField label="Responsible (email)" size="small" type="email" sx={{flex: 1}}
                               value={responsibleEmail} error={badEmail(responsibleEmail)}
                               helperText="Optional; defaults to you"
                               onChange={e => setResponsibleEmail(e.target.value)}/>
                    <TextField label="Approver (email)" size="small" type="email" sx={{flex: 1}}
                               value={approverEmail} error={badEmail(approverEmail)}
                               helperText="Optional; defaults to you"
                               onChange={e => setApproverEmail(e.target.value)}/>
                </Stack>
                {createM.isPending && <LinearProgress/>}
                {error && <Alert severity="error">{error}</Alert>}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button variant="text" onClick={onClose} disabled={createM.isPending}>Cancel</Button>
            <Button variant="contained" disabled={!valid || createM.isPending} onClick={() => void submit()}>
                Create initiative
            </Button>
        </DialogActions>
    </Dialog>;
};
